import {
	startOfMonth as dfStartOfMonth,
	endOfMonth as dfEndOfMonth,
	addMonths as dfAddMonths,
	isSameDay as dfIsSameDay,
	isWithinInterval,
	eachDayOfInterval,
	type Locale,
	startOfWeek,
	endOfWeek,
	format
} from 'date-fns'
import { de, enUS } from 'date-fns/locale'

export function startOfMonth(date: Date): Date {
	return dfStartOfMonth(date)
}

export function endOfMonth(date: Date): Date {
	return dfEndOfMonth(date)
}

export function getDaysInMonth(month: Date): Date[] {
	const start = startOfMonth(month)
	const end = endOfMonth(month)
	return eachDayOfInterval({ start, end })
}

export function addMonths(date: Date, count: number): Date {
	return dfAddMonths(date, count)
}

export function isSameDay(d1: Date, d2: Date): boolean {
	return dfIsSameDay(d1, d2)
}

export function isInRange(date: Date, from?: Date, to?: Date): boolean {
	if (!from) return false
	if (!to) return isSameDay(date, from)
	return isWithinInterval(date, { start: from, end: to })
}

function getDateFnsLocale(localeString: string | Locale): Locale {
	if (typeof localeString !== 'string') {
		return localeString
	}

	const langCode = localeString.split('-')[0].toLowerCase()

	const localeMap: Record<string, Locale> = {
		en: enUS,
		de: de
	}

	return localeMap[langCode] || enUS // Default to English if not found
}

export function getWeekdayNames(locale: string): string[] {
	const firstDay = 0
	// Get a date from the current week
	const now = new Date()
	// Get start of week (Sunday by default)
	const startDate = startOfWeek(now, { weekStartsOn: firstDay as 0 | 1 | 2 | 3 | 4 | 5 | 6 })
	// Get end of week (Saturday by default)
	const endDate = endOfWeek(now, { weekStartsOn: firstDay as 0 | 1 | 2 | 3 | 4 | 5 | 6 })

	// Get all days of the week
	const weekDays = eachDayOfInterval({ start: startDate, end: endDate })

	// Format each day to get its short name
	return weekDays.map((day) => format(day, 'EEE', { locale: getDateFnsLocale(locale) }))
}

/**
 * Generates an array of years for selection dropdowns
 * @param yearsBack - Number of years before the current year (default: 10)
 * @param yearsForward - Number of years after the current year (default: 10)
 * @param referenceYear - Reference year to calculate from (default: current year)
 * @returns Array of years in ascending order
 */
export function generateYearRange(
	yearsBack: number = 10,
	yearsForward: number = 10,
	referenceYear?: number
): number[] {
	const currentYear = referenceYear || new Date().getFullYear()
	const totalYears = yearsBack + yearsForward + 1 // +1 to include the reference year

	return Array.from({ length: totalYears }, (_, i) => currentYear - yearsBack + i)
}
